import React from "react";
import GatsbyImage from "gatsby-image";
import { useStaticQuery, graphql } from "gatsby";
import Layout from "../components/Layout";
import {
  DOG_DAYS_PAGE_TITLE,
  DOG_DAYS_ROUTE,
  SUPPORT_EMAIL_ADDRESS,
} from "../constants";
import LayoutContainer from "../components/LayoutContainer";
import type { CartItem, CartPageProps, Product } from "../types";
import ProductContainer from "../components/ProductContainer";

const DogDaysPage: React.FC<CartPageProps> = ({
  addToCart,
  cartItemObject,
}) => {
  const pageData = useStaticQuery(graphql`
    query {
      content {
        teachersPetMessage
      }
      allProduct {
        edges {
          node {
            category
            displayPrice
            description
            id
            multiplePriceOptions
            name
            priceOptionArray {
              key
              price
            }
            productImage {
              childImageSharp {
                fluid(maxWidth: 800) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
      dogDaysLogo: file(relativePath: { eq: "dog-days-logo.png" }) {
        childImageSharp {
          fluid(maxWidth: 640) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      gallery1: file(relativePath: { eq: "gallery_7.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 600) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      gallery2: file(relativePath: { eq: "gallery_8.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 600) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      gallery3: file(relativePath: { eq: "gallery_9.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 600) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      gallery4: file(relativePath: { eq: "gallery_10.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 600) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  const products = pageData.allProduct.edges as { node: Product }[];
  const content = pageData.content.teachersPetMessage;

  return (
    <LayoutContainer className="m-auto max-w-screen-2xl">
      <div className="lg:flex lg:flex-wrap py-8">
        <div className="lg:w-3/12">
          <div className="max-w-xs mb-4">
            <GatsbyImage fluid={pageData.dogDaysLogo.childImageSharp.fluid} />
          </div>
        </div>
        <div className="lg:w-6/12 lg:flex-grow lg:px-6 mb-4">
          <h2 className="font-bold text-2xl md:text-4xl mb-4">
            Teacher's Pet Dog Treats are back!
          </h2>
          <div dangerouslySetInnerHTML={{ __html: content }} className="mb-3" />
          <p>
            Need more info? Email us at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
              className="text-blue-700 underline"
            >
              {SUPPORT_EMAIL_ADDRESS}
            </a>
          </p>
        </div>
        <div className="lg:w-3/12">
          <div className="md:max-w-xs">
            {products
              .filter(({ node }) => node.category === "teachers-pet")
              .map(({ node }, index) => {
                return (
                  <div
                    key={`product-${index}`}
                    className="max-w-md m-auto h-full w-full"
                  >
                    <ProductContainer
                      {...node}
                      key={`product-${index}`}
                      addToCart={addToCart as (cartItem: CartItem) => void}
                      cartItemObject={cartItemObject}
                      orderLimit={50}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      <div className="pb-12">
        <h2 className="text-2xl md:text-4xl font-bold mb-4">Gallery</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((x, i) => (
            <div key={`image-${i}`}>
              <GatsbyImage
                fluid={{
                  ...pageData[`gallery${i + 1}`].childImageSharp.fluid,
                  aspectRatio: 0.75,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </LayoutContainer>
  );
};

const DogDaysPageWrapper: React.FC = () => {
  return (
    <Layout
      pageTitle={DOG_DAYS_PAGE_TITLE}
      pageURL={DOG_DAYS_ROUTE}
      pageDescription="Dog treats that your pup will love, made by Philips Academy students - learn more about our Dog Days campaign."
      addCartProps
    >
      <DogDaysPage />
    </Layout>
  );
};

export default DogDaysPageWrapper;
